import { FormInstanceOperationsDialog } from "../form-instance-operations.dialog";
import { BaseFormInstanceOperation } from "./base-form-instance-operation"
import { IFormDialogOperation } from "./i-form-dialog-operation";

/*
    THIS OPERATION IS DISTINCT FROM ALL OTHERS!
    In every other place in the codebase, when a dialog derived from AsyncJobDialogBase does its thing, it invokes an Async service
    on the server which sends back an AsyncJob. The dialog then polls the server for the completion of this job and once it is complete
    it invokes an api endpoint to get the content producred by the async job.

    However, this is NOT how form open works. Rather it's governed by a coordination of events created and handling of events in different CLIENT componenents.
    The coordination happens using CommunucationService. The components involved are AppComponent, DataCollectionResolver, FormInstanceService and
    FormRenderer.

    Here's how it all hangs together:

    1) When navigating to a Form, FormInstanceResolver invokes CommunicationService.startedLoadingFormInstance(int id)
       This updates CommunicationService.aFormInstanceIsLoading which is a BehaviorSubject, meaning it can be subscribed to for updates.
       This means that, via CommunicationService, any component can subscribe to this event.

    2) AppComponent.openFormDialogIfNavigatingtoForm() is called from AppComponent's constructor. It inspects the url being navigated to and if it's to 
       a FormInstance it triggers the opening of the "Open Form" dialog, and hence the running of OpenFormOperation

    3) In various components calls like this are made...
       this.communicationService.UpdateAsyncFormInstanceLoadingMessage("Getting site data from the server...5%");
       ... which update CommunicationService.AsyncFormInstanceLoadingMessage. AsyncFormInstanceLoadingMessage is a BehaviorSubject, and it is
       subscribed to by a number of components interested in these messages (including this class)

    4) The present class is interested in these messages coming from the client, and also messages from the server.
       performOperation() calls startPollingForClientLogMessages() which subscribes to ...
       this.dialog.initInfo.aFormInstanceIsLoadingMessage.subscribe(msg => { ... });
       As messages come in, they get presented to the user on the dialog box in its log

       performOperation() also calls startPollingForServerLogMessages(); which starts polling the server using the typical setInternal()
       approach. Messages that come in from the server also get inserted into the log presented to the user

       Note: No metrics model is involved in this, unlike every other aysnc dialog (and unlike the other Form Operation classes)

    5) Penultimately, when the Form has loaded, FormRenderer makes this call 
       this.communicationService.cancelAnyOpenFormDialog();

    6) Finally, this code in AppComponent listens for a null value of CommunicationService.AFormInstanceIsLoading and closes the dialog...
       this.communicationService.AFormInstanceIsLoading.subscribe((id: number) => {
            if (id == null) {
                FormInstanceOperationsDialogManager.closeFormDialog();
            }
       });
*/
export class OpenFormOperation extends BaseFormInstanceOperation implements IFormDialogOperation {

    public get title(): string {
        return "Open Form";
    }
    // Override defaults
    public showElapsedTime: boolean = false;
    public alwaysShowLog: boolean = true;
    public performAutomatically: boolean = true;
    constructor(dialog: FormInstanceOperationsDialog) {
        super(dialog);
        this.dialog.FormInstanceId = this.dialog.initInfo.formInstanceId; // <=== really strange thing to do here
    }

    // This method does not actually initiate the opening of the FormInstance, rather it subscribes to an rxjs Subject in order to
    // get messages conveying information about the loading of the Form (the percentage complete messages that get displayed to the user)
    public performOperation(): void {
        this.dialog.StartMonitoringFormOpenAt = new Date().toUTCString();
        // As messages are receieved, update the dialog so it can update its display to the user
        this.startPollingForClientLogMessages();
        this.startPollingForServerLogMessages(); // The messages obtained from the server are not currently being used in the dialog
                                                 // so maybe this whole thing should be removed and we only deal with client-side logs
    }

    private startPollingForClientLogMessages() {
        this.dialog.initInfo.aFormInstanceIsLoadingMessage.subscribe(msg => {
            if (msg) {
                // A little crude but it gets the job done. Update the dialog's properties which are used to display percentage complete
                // messages to the user. Because of how the message is constructed in the dialog, we need to set seperately set "before" server
                // and "after" server properties
                if (msg.substring(0, 6) == 'BEFORE') {
                    this.dialog.MessageFromClientBeforeServerCall = `${this.dialog.MessageFromClientBeforeServerCall} \n${removePrefix(msg)}`;
                } else {
                    this.dialog.MessageFromClientAfterServerCall = `${this.dialog.MessageFromClientAfterServerCall} \n${removePrefix(msg)}`;
                }
            }
        });

        // Removes prefixes of "BEFORE: " and "AFTER: "
        function removePrefix(msg: string) {
            if (msg.substring(0, 8) == 'BEFORE: ') {
                return msg.substring(8);
            } else if (msg.substring(0, 7) == 'AFTER: ') {
                return msg.substring(7);
            } else {
                return msg;
            }
        }
    }

    public get showStartOperationManuallyButton(): boolean {
        return false;
    };

    public get showDismissButton(): boolean {
        return false;
    }

    public get showGoToSiteHomePageButton(): boolean {
        return false;
    }

    public get showDismissWithReloadButton(): boolean {
        return false;
    }

    public startPollingForServerLogMessages() {
        this.dialog.IntervalTimeoutId = setInterval(this.formLoadIntervalTimeoutCallback, this.dialog.TimeoutTimeInMilliseconds);
    }

    // Because on Form Open we don't use the AsyncJob approach (it didn't work for this) there's no checking of JobCompleted, JobInProgress, etc
    public get Status() {
        return 'Your form is opening, please wait...';
    }

    // Note, the closing of the "Form Opening" dialog is not done here, it's done SideNavComponent.ngOnInit()
    // all that's done here is logging
    private formLoadIntervalTimeoutCallback = (): void => {
        this.dialog.TimeoutsSinceJobInitiated++;
        //console.log('TimeoutsSinceJobInitiated=' + this.dialog.TimeoutsSinceJobInitiated);
        if (this.dialog.TimeoutsSinceJobInitiated % 2 == 0) {
            this.dialog.asyncFormInstanceSaveService.getFormInstanceOpenLogEntries(this.dialog.FormInstanceId, this.dialog.StartMonitoringFormOpenAt).then(x => {
                this.dialog.FormOpenMessagesFromServer = x.join('\n');
            });
        }
    };
}
